import { defineStore } from 'pinia'

const CANONICAL_PERMISSIONS = {
  'tikuadapter.read.all': 'TikuAdapter.Read.All',
  'tikuadapter.write.all': 'TikuAdapter.Write.All',
  'tikuadapter.execute.all': 'TikuAdapter.Execute.All',
  'tikuadapter.mgt.all': 'TikuAdapter.Mgt.All',
}

function normalizeBool(value, fallback = false) {
  if (value === undefined || value === null) return fallback
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') return value.toLowerCase() === 'true'
  return Boolean(value)
}

function normalizeNumber(value, fallback = 0) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export const useSessionStore = defineStore('session', {
  state: () => ({
    accessToken: '',
    userToken: '',
    authUserId: '',
    username: '',
    displayName: '',
    email: '',
    permissions: [],
    requestLimit: 0,
    requestUsed: 0,
    allowAi: true,
    allowPaid: true,
  }),
  getters: {
    remainingRequests(state) {
      if (state.requestLimit < 0) return -1
      return Math.max(state.requestLimit - state.requestUsed, 0)
    },
  },
  actions: {
    clear() {
      this.$reset()
    },
    applyGatewayProfile(data = {}) {
      const authUserId = data.sub || data.username || data.preferred_username || ''
      const username = data.username || data.preferred_username || data.email || authUserId
      const displayName = data.nickname || data.name || username || authUserId
      const accessToken = normalizeAccessToken(data.accessToken || data.access_token || data.token || '')
      const userToken = normalizeUserToken(data.usertoken || data.userToken || data.user_token || '')
      if (accessToken) this.accessToken = accessToken
      if (userToken) this.userToken = userToken
      this.authUserId = authUserId
      this.username = username
      this.displayName = displayName
      this.email = data.email || ''
      this.permissions = normalizePermissions(data.permissions)
    },
    applyUserSettings(data = {}) {
      const user = data?.user || {}
      if (user.authUserId !== undefined) this.authUserId = user.authUserId || this.authUserId
      if (user.requestLimit !== undefined) this.requestLimit = normalizeNumber(user.requestLimit, 0)
      if (user.requestUsed !== undefined) this.requestUsed = normalizeNumber(user.requestUsed, 0)
      if (user.allowAi !== undefined) this.allowAi = normalizeBool(user.allowAi, true)
      if (user.allowPaid !== undefined) this.allowPaid = normalizeBool(user.allowPaid, true)
    },
    setQuota(requestLimit, requestUsed) {
      if (requestLimit !== undefined) this.requestLimit = normalizeNumber(requestLimit, this.requestLimit)
      if (requestUsed !== undefined) this.requestUsed = normalizeNumber(requestUsed, this.requestUsed)
    },
    setEntitlements({ allowAi, allowPaid } = {}) {
      if (allowAi !== undefined) this.allowAi = normalizeBool(allowAi, this.allowAi)
      if (allowPaid !== undefined) this.allowPaid = normalizeBool(allowPaid, this.allowPaid)
    },
    setPermissions(permissions = []) {
      this.permissions = normalizePermissions(permissions)
    },
    setAccessToken(token = '') {
      this.accessToken = normalizeAccessToken(token)
    },
    setUserToken(token = '') {
      this.userToken = normalizeUserToken(token)
    },
  },
})

function normalizeAccessToken(token) {
  const value = String(token || '').trim()
  if (!value) return ''
  if (value.toLowerCase().startsWith('bearer ')) {
    return value.slice(7).trim()
  }
  return value
}

function normalizeUserToken(token) {
  return String(token || '').trim()
}

function normalizePermissions(values) {
  if (!Array.isArray(values)) return []
  const mapped = values.map((item) => canonicalPermission(item)).filter(Boolean)
  return [...new Set(mapped)]
}

function canonicalPermission(permission) {
  const raw = String(permission || '').trim()
  const value = raw.toLowerCase()
  if (!value) return ''
  if (value === '*' || value === 'admin') return value
  return CANONICAL_PERMISSIONS[value] || raw
}
